import { fromPromise } from '@apollo/client';
import { getLocalStorage, saveAuthInfo, removeAuthInfo, createSnack } from 'common/utils/general';
import routes from 'common/routes';
import auth from 'api/graphql/auth';
import { persistor, store } from 'store';
import i18n from 'i18n';
import logger from 'services/logger';
import uiSlice from 'store/modules/ui';

// Un solo refresh en vuelo por contexto JS: todas las operaciones que reciban 401 en
// paralelo (el dashboard dispara varias al abrir la PWA con el token vencido) se
// encadenan a la MISMA promesa en vez de competir por rotar el refresh token.
let refreshPromise = null;

const AUTH_ERROR_MESSAGES = ['Unauthenticated.', 'Not Authenticated'];
const isAuthErrorMessage = (message) => AUTH_ERROR_MESSAGES.includes(message);

// Operaciones que nunca deben disparar un refresh (evita recursión y falsos positivos).
const REFRESH_EXEMPT_OPERATIONS = ['refreshToken', 'login', 'selfRegister'];

// Lecturas de fondo no críticas (contadores/dashboard/listas de sesiones): degradan a 0/vacío en la UI.
// Si fallan (transitorio, permiso por rol, o ERROR PARCIAL por un registro semilla con
// entrenador/cliente/tipo soft-deleted que dispara un campo non-null) NO deben ensuciar la consola ni
// mostrar el toast "Oops": Apollo igual entrega la data parcial y la lista se renderiza normal.
const NON_BLOCKING_OPERATIONS = [
  'sessionCounters',
  'sessionDashboard',
  'clientPaymentCounters',
  'sessions',
  'sessionsAll',
  'myGroupSessions',
  'groupSessions',
  'myPrograms',
  'myRecipeBooks',
  'myIntake',
  'nutritionPrograms',
  'recipeBooks',
  'clientIntake',
  'MyPhysicalProgress',
  // Check-in de bienestar: la card se oculta sola si la lectura falla y el modal re-consulta
  // esta misma query dentro de su propio catch. Un toast rojo aquí sería un error visible en
  // el Inicio por una card que la mayoría de usuarios ni siquiera iba a ver.
  'pendingWellbeingCheckin'
];

const isNonBlockingOperation = (operation) =>
  NON_BLOCKING_OPERATIONS.includes(operation?.operationName);

// Deduplicación de notificaciones de error
const recentErrors = new Map();
const ERROR_DEDUP_WINDOW = 1000; // 1 segundo

/**
 * Limpiar errores antiguos del mapa de deduplicación
 */
const cleanupRecentErrors = () => {
  const now = Date.now();
  // Eliminar errores más antiguos que la ventana de deduplicación
  Array.from(recentErrors.entries()).forEach(([message, timestamp]) => {
    if (now - timestamp > ERROR_DEDUP_WINDOW) {
      recentErrors.delete(message);
    }
  });
};

/**
 * Verificar si ya se mostró este error recientemente
 */
const isDuplicateError = (message) => {
  cleanupRecentErrors();

  if (recentErrors.has(message)) {
    return true;
  }

  recentErrors.set(message, Date.now());
  return false;
};

/**
 * Mostrar notificación de error al usuario
 */
const showErrorNotification = (message) => {
  // Nunca encolar un toast sin texto: un error parcial de GraphQL (lectura de fondo que igual
  // entrega data) producía un bar rojo VACÍO. Si no hay mensaje legible, no se muestra nada.
  if (!message || !String(message).trim()) {
    return;
  }

  // Evitar mostrar el mismo error múltiples veces en rápida sucesión
  if (isDuplicateError(message)) {
    return;
  }

  const snack = createSnack({
    key: `error-${Date.now()}`,
    variant: 'error',
    message
  });

  store.dispatch(uiSlice.actions.enqueueSnackbar(snack));
};

/**
 * Obtener mensaje de error amigable para el usuario
 */
const getUserFriendlyMessage = (error, operation) => {
  // Errores de autenticación
  if (error.message === 'Unauthenticated.' || error.message === 'Not Authenticated') {
    return null; // No mostrar mensaje, se redirige al login
  }

  // Error de login
  if (operation?.operationName === 'login') {
    return i18n.t('auth.wrongCredentials', { ns: 'errors' });
  }

  // Email duplicado
  if (
    error.message?.includes('Duplicate entry') ||
    error.message?.includes('This email is already registered') ||
    error.message?.toLowerCase().includes('email') && error.message?.toLowerCase().includes('unique') ||
    error.message?.toLowerCase().includes('email') && error.message?.toLowerCase().includes('registered') ||
    error.message?.includes('users_email_unique') ||
    error.extensions?.validation?.email ||
    error.extensions?.validation?.['input.email']
  ) {
    return i18n.t('general.duplicateEmail', { ns: 'errors' });
  }

  // Errores de validación
  if (error.extensions?.validation) {
    return i18n.t('general.validationError', { ns: 'errors' });
  }

  // Errores de permisos
  if (error.extensions?.category === 'authorization' || error.message?.includes('authorized')) {
    return i18n.t('general.unauthorized', { ns: 'errors' });
  }

  // Error 404
  if (error.extensions?.category === 'graphql' || error.message?.includes('not found')) {
    return i18n.t('general.notFound', { ns: 'errors' });
  }

  // Error de servidor (500)
  if (error.extensions?.category === 'internal' || error.message?.includes('Internal')) {
    return i18n.t('general.serverError', { ns: 'errors' });
  }

  // Error genérico
  return i18n.t('general.unexpected', { ns: 'errors' });
};

const getErrors = (data) => {
  let errors = [];

  if (Object.keys(data).length > 0) {
    Object.keys(data).forEach((key) => {
      const message = data[key];

      if (Array.isArray(message)) {
        errors = [...errors, message];
      } else {
        errors.push(message);
      }
    });

    return errors;
  }

  return ['Internal Error'];
};

const redirectToLogin = async () => {
  // Solo credenciales y estado persistido: NO localStorage.clear() (se llevaba el tema,
  // el tour visto y los borradores de contenido en cada expiración de sesión).
  await removeAuthInfo();
  persistor.purge();
  // Conserva a dónde iba el usuario para volver tras el login (solo rutas internas).
  const target = `${window.location.pathname}${window.location.search}`;
  const redirect =
    target && target !== '/' && !target.startsWith(routes.login.path)
      ? `?redirect=${encodeURIComponent(target)}`
      : '';
  // routes.login.path a secas: REACT_APP_BASE_URL no existe en ningún build y el template
  // producía la ruta RELATIVA 'undefined/login', una página en blanco sin ruta.
  window.location.replace(`${routes.login.path}${redirect}`);
};

/**
 * Renueva la sesión con el refresh token guardado.
 *
 * Passport ROTA el refresh token en cada uso (un solo uso): si otra instancia (la PWA y
 * una pestaña abiertas a la vez) ya renovó y guardó tokens nuevos, el rechazo de NUESTRO
 * refresh viejo no significa que la sesión murió — se detecta releyendo el storage y se
 * continúa con la sesión fresca en vez de cerrar la sesión del usuario.
 */
const refreshSession = async () => {
  const storedRefreshToken = await getLocalStorage('otx_r');

  if (!storedRefreshToken) {
    const error = new Error('missing-refresh-token');
    error.fatal = true;
    throw error;
  }

  try {
    const { data } = await auth.refreshToken(storedRefreshToken);
    await saveAuthInfo(data.refreshToken);
    // Dentro del cascarón nativo, el móvil guarda su propia copia de los tokens (para
    // re-sembrar la web si pierde su almacén). Passport ROTA el refresh en cada uso, así
    // que sin este aviso la copia nativa queda revocada tras la primera renovación.
    try {
      if (window.ReactNativeWebView && /OtrixApp/.test(window.navigator.userAgent)) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            type: 'tokens-rotated',
            access_token: data.refreshToken.access_token,
            refresh_token: data.refreshToken.refresh_token
          })
        );
      }
    } catch (ex) {
      // el aviso es best-effort: jamás debe romper la renovación
    }
    return true;
  } catch (error) {
    const currentRefreshToken = await getLocalStorage('otx_r');
    if (currentRefreshToken && currentRefreshToken !== storedRefreshToken) {
      // Otra instancia rotó el token mientras tanto: ya hay tokens frescos guardados.
      return true;
    }
    if (!error?.networkError) {
      // El servidor respondió y rechazó el refresh (expirado/revocado): sesión muerta.
      error.fatal = true;
    }
    throw error;
  }
};

/**
 * Renovación compartida: como Passport rota el refresh token en cada uso, dos renovaciones a la vez
 * se invalidan entre sí. Se exporta para que el cliente REST de pagos reutilice ESTA misma promesa
 * en vez de renovar por su cuenta (o, como hacía antes, cerrar la sesión de golpe en cualquier 401).
 */
export const getRefreshPromise = () => {
  if (!refreshPromise) {
    refreshPromise = refreshSession()
      .catch((error) => {
        if (error?.fatal) redirectToLogin();
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

const errorHandler = ({ response, operation, graphQLErrors, networkError, forward }) => {
  const nonBlocking = isNonBlockingOperation(operation);

  // Registrar error en el logger (excepto lecturas de fondo no críticas).
  if ((graphQLErrors || networkError) && !nonBlocking) {
    logger.graphQLError(operation, graphQLErrors, networkError);
  }

  // Sesión vencida (Passport expira el access token a las 24h): renovar UNA sola vez y
  // REINTENTAR la operación. El observable de forward debe retornarse SÍNCRONO desde el
  // handler — es lo único que Apollo suscribe; el forward(operation) que antes vivía
  // dentro de un forEach async se descartaba, y por eso las consultas fallaban a la
  // vista ("Unauthenticated." en pantalla) aunque el refresh sí funcionara.
  const hasAuthError = Boolean(response?.errors?.some((err) => isAuthErrorMessage(err.message)));
  if (hasAuthError && !REFRESH_EXEMPT_OPERATIONS.includes(operation?.operationName)) {
    // Se reintenta SIEMPRE, aunque la renovación falle.
    //
    // Antes había un `.filter(Boolean)` aquí: si el refresh fallaba de forma NO fatal (una caída de
    // red momentánea, típica en móvil), el observable se completaba sin emitir nada y la consulta
    // se quedaba colgada, sin datos y sin error. El componente no tenía a qué reaccionar y volvía a
    // intentarlo, que es de donde salían los reintentos infinitos. Reenviando siempre, o el
    // reintento funciona, o vuelve un error de verdad que la interfaz sí sabe mostrar. Cuando la
    // sesión está muerta de verdad, `getRefreshPromise` ya redirige al login por su cuenta.
    return fromPromise(getRefreshPromise().catch(() => false)).flatMap(() => forward(operation));
  }

  if (response && 'errors' in response && response.errors.length > 0) {
    response.errors.forEach((err, index) => {
      if ('extensions' in err && 'validation' in err.extensions) {
        const errors = getErrors(err.extensions.validation);
        response.errors[index].message = errors.join('. ');
      }

      // Actualizar mensaje de error con uno amigable
      const userFriendlyMessage = getUserFriendlyMessage(err, operation);
      if (userFriendlyMessage) {
        response.errors[index].message = userFriendlyMessage;
        // Operaciones con manejo propio de errores (saga middleware o component catch)
        // evitan el toast duplicado de Apollo
        const SAGA_MANAGED_MUTATIONS = [
          'createDynamicPerformance',
          'updateDynamicPerformance',
          'deleteDynamicPerformance',
          'updatePlan',
          'UploadPlanImage',
          'syncPlanSessionTypes',
          'verifyEmail',
          'RequestPlanAcquisition',
          // Vincular organización: la vista muestra su propio toast ("Esa organización no existe").
          'JoinOrganizationByCode',
          // T&C: legalSaga muestra su propio error (evita el doble toast del bug de "Acepto y confirmo").
          'acceptLegal',
          // Check-in de bienestar: el modal re-consulta qué slots quedan y decide si fue un
          // fallo real, un envío a medias o un envío que SÍ quedó guardado (ahí muestra la
          // pantalla de "¡Gracias!"). El toast genérico de Apollo contradecía esa pantalla.
          'submitWellbeingCheckin',
        ];
        if (
          operation?.operationName !== 'login' &&
          operation?.operationName !== 'selfRegister' &&
          !nonBlocking &&
          !SAGA_MANAGED_MUTATIONS.includes(operation?.operationName)
        ) {
          showErrorNotification(userFriendlyMessage);
        }
      }

      return null;
    });
  }

  // Manejar errores de GraphQL
  if (graphQLErrors && !nonBlocking) {
    graphQLErrors.forEach((err) => {
      // Solo mostrar en consola en desarrollo
      if (process.env.NODE_ENV === 'development') {
        console.error('[GraphQL error]:', err.message, err); // eslint-disable-line
      }
    });
  }

  // Manejar errores de red
  if (networkError) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Network error]:', networkError); // eslint-disable-line
    }

    // Mostrar mensaje amigable de error de red al usuario (no para lecturas de fondo).
    if (!nonBlocking) {
      const networkMessage = i18n.t('general.networkError', { ns: 'errors' });
      showErrorNotification(networkMessage);
    }
  }

  return null;
};

export default errorHandler;
